(() => {
  'use strict';

  // This bridge stores no click reference in browser storage. The only stored
  // value is a server-issued, revocable, opaque context ID after consent.
  const config = window.WunderMakerOpenAI;
  if (config?.enabled !== true || window.Shopify?.designMode === true) return;
  if (window.location.protocol !== 'https:') return;

  const COOKIE = 'wm_openai_context';
  const CART_ATTRIBUTE = '__wm_openai_context';
  const ENDPOINT = '/apps/wundermaker/openai-context';
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'campaign_id', 'ad_group_id', 'ad_id'];
  const ATTRIBUTION_TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;
  const DENIED = { analytics: false, marketing: false, sale_of_data: false };
  const knownContexts = new Set();
  const revocationsInFlight = new Set();
  const synchronizingEpochs = new Set();
  let currentId = null;
  let allowed = null;
  let epoch = 0;
  let flow = Promise.resolve();
  let cartFlow = Promise.resolve();
  let loadAttempts = 0;
  let deniedByPeer = false;
  let consentChannel = null;
  // Memory only, so an asynchronous privacy API load cannot lose the original
  // landing query. Explicit denial/revocation discards it; it is never logged.
  let landingQuery = window.location.search;

  const readCookie = () => {
    try {
      const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${COOKIE}=`));
      const value = entry ? decodeURIComponent(entry.slice(COOKIE.length + 1)) : '';
      return UUID.test(value) ? value : null;
    } catch (_error) {
      return null;
    }
  };

  const clearCookie = () => {
    document.cookie = `${COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
  };

  const consent = () => {
    if (deniedByPeer) return { ...DENIED };
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return {
        analytics: privacy?.analyticsProcessingAllowed?.() === true,
        marketing: privacy?.marketingAllowed?.() === true,
        sale_of_data: privacy?.saleOfDataAllowed?.() === true,
      };
    } catch (_error) {
      return { ...DENIED };
    }
  };

  const permitted = (value) => value.analytics && value.marketing && value.sale_of_data;
  const stillAllowed = (requestEpoch) => requestEpoch === epoch && allowed === true && permitted(consent());

  const sourceUrl = () => {
    const url = new URL(window.location.href);
    // Account/order/customizer-session URLs must never become advertising data.
    const publicPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:(?:products|collections|pages)\/[a-z0-9-]+\/?|cart\/?)?$/i;
    return `${url.origin}${publicPath.test(url.pathname) ? url.pathname : '/'}`;
  };

  const landingAttribution = () => {
    const query = new URLSearchParams(landingQuery || '');
    const result = {};
    const references = query.getAll('oppref');
    if (references.length === 1 && references[0].length > 0 && references[0].length <= 2048 && !/[\u0000-\u001f\u007f]/.test(references[0])) {
      result.oppref = references[0];
    }
    const attribution = {};
    ATTRIBUTION_KEYS.forEach((key) => {
      const values = query.getAll(key);
      if (values.length === 1 && ATTRIBUTION_TOKEN.test(values[0])) attribution[key] = values[0];
    });
    if (Object.keys(attribution).length) result.attribution = attribution;
    return result;
  };

  const post = async (path, body, readJson = true) => {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10000);
    try {
      const response = await window.fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: abort.signal,
        keepalive: true,
      });
      if (!response.ok) throw new Error('Tracking bridge request failed');
      return readJson ? await response.json() : null;
    } finally {
      clearTimeout(timeout);
    }
  };

  const cartPath = () => {
    // Shopify.routes.root includes the active market/language prefix.
    const root = window.Shopify?.routes?.root;
    return typeof root === 'string' && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root)
      ? `${root}cart/update.js`
      : '/cart/update.js';
  };

  const writeCart = (id, requestEpoch) => {
    const operation = cartFlow.catch(() => {}).then(async () => {
      // A queued positive write can become obsolete while waiting for a cart
      // request. Re-evaluate immediately before transmission.
      const value = id && stillAllowed(requestEpoch) ? id : '';
      await post(cartPath(), { attributes: { [CART_ATTRIBUTE]: value } }, false);
    });
    cartFlow = operation;
    return operation;
  };

  const revoke = async (id) => {
    if (!id || revocationsInFlight.has(id)) return;
    revocationsInFlight.add(id);
    try {
      await post(ENDPOINT, { context_id: id, consent: { ...DENIED }, source_url: sourceUrl() });
      knownContexts.delete(id);
    } catch (_error) {
      // Keep the ID in memory for a later lifecycle/consent retry. No cookie or
      // cart attribution is restored when a privacy cleanup request fails.
    } finally {
      revocationsInFlight.delete(id);
    }
  };

  const publishReady = (requestEpoch, id, attempt = 0) => {
    if (!stillAllowed(requestEpoch) || readCookie() !== id) return;
    const retry = () => {
      if (attempt < 19) setTimeout(() => publishReady(requestEpoch, id, attempt + 1), 250);
    };
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') {
        retry();
        return;
      }
      Promise.resolve(publish.call(window.Shopify.analytics, 'wundermaker:openai_context_ready', { schema_version: 1 })).catch(retry);
    } catch (_error) {
      // Analytics initialization can lag this asset; retry only briefly and
      // never announce a context after consent or cookie identity changes.
      retry();
    }
  };

  const synchronize = async (requestEpoch, attempt = 0) => {
    if (!stillAllowed(requestEpoch)) return;
    try {
      const existingId = readCookie() || currentId;
      if (existingId) knownContexts.add(existingId);
      const response = await post(ENDPOINT, {
        context_id: existingId,
        consent: consent(),
        source_url: sourceUrl(),
        ...landingAttribution(),
      });
      const id = typeof response?.context_id === 'string' && UUID.test(response.context_id) ? response.context_id : null;
      if (!id) {
        clearCookie();
        currentId = null;
        await writeCart(null, requestEpoch);
        return;
      }
      knownContexts.add(id);
      // Never adopt a late response from a request sent before revocation.
      if (!stillAllowed(requestEpoch)) {
        await revoke(id);
        return;
      }
      currentId = id;
      clearCookie();
      await writeCart(id, requestEpoch);
      if (!stillAllowed(requestEpoch)) {
        await revoke(id);
        await writeCart(null, epoch);
        return;
      }
      document.cookie = `${COOKIE}=${encodeURIComponent(id)}; Path=/; Max-Age=2592000; Secure; SameSite=Lax`;
      landingQuery = '';
      // A blocked cookie prevents the sandbox collector from seeing the ID.
      if (readCookie() === id) publishReady(requestEpoch, id);
    } catch (_error) {
      // Retry transient proxy/cart failures briefly within the same consent
      // epoch. Revocation cancels these timers logically before transmission.
      if (stillAllowed(requestEpoch) && attempt < 2) {
        setTimeout(() => queueSync(requestEpoch, attempt + 1), 500 * (2 ** attempt));
      }
    }
  };

  const queueSync = (requestEpoch, attempt = 0) => {
    if (!stillAllowed(requestEpoch) || synchronizingEpochs.has(requestEpoch)) return;
    synchronizingEpochs.add(requestEpoch);
    flow = flow.catch(() => {}).then(() => synchronize(requestEpoch, attempt)).finally(() => {
      synchronizingEpochs.delete(requestEpoch);
    });
  };

  const refresh = (explicitConsentChange = false, force = false) => {
    const nextAllowed = permitted(consent());
    if (explicitConsentChange && !nextAllowed) landingQuery = '';
    if (nextAllowed === allowed && !force && !explicitConsentChange) return;
    const wasAllowed = allowed;
    allowed = nextAllowed;
    if (wasAllowed !== nextAllowed) epoch += 1;
    const requestEpoch = epoch;
    if (!nextAllowed) {
      const stored = readCookie();
      if (stored) knownContexts.add(stored);
      if (currentId) knownContexts.add(currentId);
      currentId = null;
      clearCookie();
      if ((wasAllowed === true || stored || knownContexts.size) && !deniedByPeer) {
        try { consentChannel?.postMessage({ revoked: true }); } catch (_error) { /* Optional browser API. */ }
      }
      // Cart writes are serialized so an earlier positive write cannot be the
      // last write after a withdrawal. Server revocation is independent.
      if (stored || knownContexts.size) writeCart(null, requestEpoch).catch(() => {});
      knownContexts.forEach((id) => { void revoke(id); });
      return;
    }
    queueSync(requestEpoch);
  };

  const ensurePrivacyApi = () => {
    if (window.Shopify?.customerPrivacy) {
      refresh();
      return;
    }
    const loadFeatures = window.Shopify?.loadFeatures;
    if (typeof loadFeatures === 'function') {
      try {
        loadFeatures.call(window.Shopify, [{ name: 'consent-tracking-api', version: '0.1' }], () => refresh());
      } catch (_error) {
        refresh();
      }
      return;
    }
    // Shopify bootstrapping can lag a deferred asset. Bound the startup retry.
    loadAttempts += 1;
    if (loadAttempts < 20) setTimeout(ensurePrivacyApi, 250);
    else refresh();
  };

  // A sibling tab can retain stale in-memory privacy state. Withdrawal wins
  // until this tab receives its own positive Shopify consent change.
  try {
    if (typeof window.BroadcastChannel === 'function') {
      consentChannel = new window.BroadcastChannel('wm-openai-consent');
      consentChannel.onmessage = (event) => {
        if (event.data?.revoked === true) {
          deniedByPeer = true;
          refresh(true, true);
        }
      };
    }
  } catch (_error) { /* Focus and lifecycle rechecks remain available. */ }
  document.addEventListener('visitorConsentCollected', () => {
    deniedByPeer = false;
    refresh(true);
  });
  window.addEventListener('focus', () => refresh());
  window.addEventListener('online', () => refresh(false, true));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(false, true); });
  window.addEventListener('pagehide', () => {
    if (!permitted(consent())) {
      refresh(true, true);
      knownContexts.forEach((id) => { void revoke(id); });
    }
  });
  ensurePrivacyApi();
})();
